import React, { useState } from 'react';
import { 
  Trash2, 
  Image as ImageIcon, 
  Upload, 
  Loader2,
  FileText
} from 'lucide-react';
import { AppointmentSummaryResponse, Prescription } from './types';
import { PatientService } from '../../../services/api/patient.service';
import toast from 'react-hot-toast';

interface PrescriptionsTabProps {
  data: AppointmentSummaryResponse['appointmentSummary'];
  reload: () => void;
}

const PrescriptionsTab: React.FC<PrescriptionsTabProps> = ({ data, reload }) => {
  const { appointmentSummary: nestedSummary, appointment } = data;
  const prescriptions = nestedSummary.prescriptions || [];
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    formData.append("appointmentId", appointment.id);
    formData.append("patientId", appointment.patientId);
    
    Array.from(files).forEach((file) => {
      formData.append("image", file);
    });

    try {
      setUploading(true);
      const response = await PatientService.addPrescription(formData);
      if (response.success) {
        toast.success("Prescription uploaded successfully");
        reload();
      } else {
        toast.error(response.message || "Failed to upload prescription");
      }
    } catch (error) {
      toast.error("An error occurred while uploading");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this prescription?")) return;
    
    try {
      setDeleting(id);
      const response = await PatientService.deletePrescription(id);
      if (response.success) {
        toast.success("Prescription deleted successfully");
        reload();
      } else {
        toast.error(response.message || "Failed to delete prescription");
      }
    } catch (error) {
      toast.error("An error occurred while deleting");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between border-b border-gray-100 pb-6">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-emerald-600" />
          <h2 className="text-xl font-bold text-gray-900">Patient Prescriptions</h2>
        </div>
        
        <label className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-md cursor-pointer disabled:bg-gray-400">
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
          {uploading ? 'Uploading...' : 'Upload New'}
          <input 
            type="file" 
            multiple 
            accept="image/*,.pdf" 
            onChange={handleFileUpload} 
            className="hidden" 
            disabled={uploading}
          />
        </label>
      </div>

      {prescriptions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
          <ImageIcon className="h-16 w-16 text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">No prescriptions found for this appointment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {prescriptions.map((presc: Prescription) => {
            const isPdf = presc.medicationDocPath && presc.medicationDocPath.toLowerCase().includes('.pdf');
            return (
            <div key={presc.id} className="group relative bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-all">
              <div 
                className="aspect-[4/3] bg-gray-100 flex items-center justify-center cursor-pointer"
                onClick={() => {
                  if (presc.medicationDocPath) {
                    window.open(presc.medicationDocPath, '_blank');
                  }
                }}
              >
                {presc.medicationDocPath ? (
                  isPdf ? (
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <FileText className="h-12 w-12 mb-2 text-rose-500" />
                      <span className="text-sm font-medium">PDF Document</span>
                    </div>
                  ) : (
                    <img 
                      src={presc.medicationDocPath} 
                      alt="Prescription" 
                      className="w-full h-full object-cover"
                    />
                  )
                ) : (
                  <ImageIcon className="h-12 w-12 text-gray-300" />
                )}
              </div>
              
              <div className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-gray-900">Prescription</p>
                  <p className="text-[10px] text-gray-500 font-medium">
                    {new Date(presc.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(presc.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  disabled={deleting === presc.id}
                >
                  {deleting === presc.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PrescriptionsTab;
